import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { HomeScreenPrompt } from "@/components/ui/home-screen-prompt";
import { Info, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Check for password reset session and current user on component mount
  useEffect(() => {
    const checkAuthState = async () => {
      console.log('Checking auth state...');
      
      // First check the URL for reset tokens
      const hashFragment = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);
      
      console.log('Hash fragment:', hashFragment);
      console.log('URL params:', urlParams.toString());
      
      // Check for password reset tokens in URL hash
      if (hashFragment && hashFragment.includes('access_token') && hashFragment.includes('type=recovery')) {
        console.log('Password reset tokens detected in URL');
        const hashParams = new URLSearchParams(hashFragment.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');
        
        if (accessToken && type === 'recovery') {
          console.log('Valid recovery tokens found, setting up password reset');
          setIsPasswordReset(true);
          
          // Clear any existing session first
          await supabase.auth.signOut();
          setCurrentUser(null);
          
          // Set the session for password reset
          try {
            const refreshToken = hashParams.get('refresh_token') || '';
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('Error setting reset session:', error);
              toast({
                title: "Invalid reset link",
                description: "The password reset link is invalid or has expired. Please request a new one.",
                variant: "destructive",
              });
              setIsPasswordReset(false);
              window.location.hash = '';
              return;
            }
            
            console.log('Reset session established successfully');
            toast({
              title: "Password reset ready",
              description: "You can now set your new password below.",
            });
            
            // Don't check for existing user session when in reset mode
            return;
          } catch (error) {
            console.error('Error during reset session setup:', error);
            toast({
              title: "Error",
              description: "An error occurred while processing the reset link.",
              variant: "destructive",
            });
            setIsPasswordReset(false);
            window.location.hash = '';
            return;
          }
        }
      }
      
      // Only check for existing session if NOT in password reset mode
      if (!isPasswordReset) {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Current session:', session ? 'exists' : 'none');
        setCurrentUser(session?.user || null);
      }
    };
    
    checkAuthState();
  }, [toast]);

  // Redirect authenticated users to dashboard (unless they're in password reset mode)
  useEffect(() => {
    if (currentUser && !isPasswordReset) {
      console.log('Redirecting authenticated user to dashboard');
      navigate("/");
    }
  }, [currentUser, isPasswordReset, navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Attempting to sign in with:", { email, password: "***" });
      console.log("Supabase URL check: Starting authentication process...");
      console.log("Network connectivity test...");
      
      // Test basic network connectivity first
      try {
        const response = await fetch('https://www.google.com/generate_204', { 
          method: 'GET',
          mode: 'no-cors' 
        });
        console.log("Network test: OK");
      } catch (netError) {
        console.error("Network test failed:", netError);
        toast({
          title: "Network Error",
          description: "Unable to connect to the internet. Please check your connection.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      
      console.log("Attempting Supabase auth...");
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log("Supabase auth response:", { data, error: signInError });

      if (signInError) {
        console.error("Sign in error:", signInError);
        console.error("Error details:", {
          message: signInError.message,
          status: signInError.status,
          statusCode: signInError.status,
          name: signInError.name
        });
        
        if (signInError.message.includes("Invalid login")) {
          toast({
            title: "Authentication error",
            description: "Invalid email or password. If you're a client, please use the credentials provided by your administrator.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Authentication error",
            description: signInError.message,
            variant: "destructive",
          });
        }
        
        setIsLoading(false);
        return;
      }
      
      console.log("Login successful:", data);
      
      // Fetch user roles directly from user_roles table
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', data.user.id);
      
      if (rolesError) {
        console.error("Error fetching roles:", rolesError);
      } else {
        console.log("User roles:", roles);
      }
      
      navigate("/");
    } catch (error: any) {
      console.error("Unexpected auth error:", error);
      toast({
        title: "Authentication error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address to reset your password",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const redirectTo = `${window.location.origin}/auth`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo,
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Password reset email sent",
        description: "Check your email for the password reset link. The link will redirect you back to this page.",
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        title: "Password reset failed",
        description: error.message || "Failed to send password reset email",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please ensure both password fields match",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('Updating password...');
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        throw error;
      }
      
      console.log('Password updated successfully');
      toast({
        title: "Password updated successfully",
        description: "Your password has been updated. You are now logged in.",
      });
      
      // Clear the URL hash and reset state
      window.location.hash = '';
      setIsPasswordReset(false);
      
      // Get the new session
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUser(session?.user || null);
      
      // Redirect to dashboard
      navigate("/");
      
    } catch (error: any) {
      console.error("Password update error:", error);
      toast({
        title: "Password update failed",
        description: error.message || "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 px-4 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 dark:bg-blue-900/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-100 dark:bg-indigo-900/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-32 left-20 w-72 h-72 bg-blue-50 dark:bg-blue-800/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <Card className="w-full max-w-md p-8 space-y-8 backdrop-blur-sm bg-white/95 dark:bg-slate-900/95 border border-white/20 dark:border-slate-700/50 shadow-2xl relative z-10">
        {/* Logo Section */}
        <div className="flex justify-center mb-6">
          <img 
            src="/ovit-logo.png" 
            alt="OVIT" 
            className="h-20 w-20 object-contain drop-shadow-lg"
          />
        </div>
        
        {/* Header Section */}
        <div className="space-y-3 text-center">
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
            {isPasswordReset ? 'Set New Password' : 'Welcome back'}
          </h1>
          <p className="text-base text-gray-600 dark:text-gray-300">
            {isPasswordReset ? 'Enter your new password below' : 'Sign in to access your dashboard'}
          </p>
        </div>

        {/* Password Reset Success Alert */}
        {new URLSearchParams(window.location.search).get('reset') === 'true' && !isPasswordReset && (
          <Alert className="bg-green-50/80 dark:bg-green-950/30 border-green-200/60 dark:border-green-800/40 backdrop-blur-sm">
            <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle className="text-green-800 dark:text-green-300">Password Reset Link Received</AlertTitle>
            <AlertDescription className="text-green-700 dark:text-green-300">
              Please check your email for a password reset link. Click the link in your email to set a new password.
            </AlertDescription>
          </Alert>
        )}

        {/* Tips Section */}
        {showTips && !isPasswordReset && (
          <Alert className="bg-blue-50/80 dark:bg-blue-950/30 border-blue-200/60 dark:border-blue-800/40 backdrop-blur-sm">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle className="text-blue-800 dark:text-blue-300">Client Login Information</AlertTitle>
            <AlertDescription>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                If you're a client logging in for the first time, your password format is typically:
              </p>
              <ul className="list-disc ml-5 text-sm mt-2 text-blue-700 dark:text-blue-300">
                <li>Starts with a "C" followed by the last 4 digits of your phone</li>
                <li>Followed by "#Client123 | www.shiftask.app"</li>
                <li>For example: "C1234#Client123 | www.shiftask.app"</li>
              </ul>
              <p className="text-sm mt-2 text-blue-700 dark:text-blue-300">
                If you can't log in, please contact your administrator or use the password reset option.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Forms */}
        {isPasswordReset ? (
          /* New Password Form */
          <form onSubmit={handleNewPasswordSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="h-12 text-base pr-12 transition-all duration-200"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Confirm New Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 text-base pr-12 transition-all duration-200"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 dark:from-blue-500 dark:to-indigo-500 dark:hover:from-blue-600 dark:hover:to-indigo-600 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" 
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Updating password...
                </div>
              ) : (
                "Update Password"
              )}
            </Button>
          </form>
        ) : (
          /* Login Form */
          <form onSubmit={handleAuth} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 text-base transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Password
                </Label>
                <button 
                  type="button" 
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors duration-200"
                  onClick={handlePasswordReset}
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base pr-12 transition-all duration-200"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 dark:from-blue-500 dark:to-indigo-500 dark:hover:from-blue-600 dark:hover:to-indigo-600 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" 
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Signing in...
                </div>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        )}

        {/* Help Section - Only show for login form */}
        {!isPasswordReset && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowTips(!showTips)}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors duration-200 flex items-center gap-2 mx-auto"
            >
              <Info className="h-4 w-4" />
              {showTips ? "Hide" : "Show"} login help
            </button>
          </div>
        )}
      </Card>
      
      <HomeScreenPrompt />

      <style>
        {`
          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(30px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }
          
          .animate-blob {
            animation: blob 7s infinite;
          }
          
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `}
      </style>
    </div>
  );
};

export default Auth;
